import { createRouter, createWebHistory } from 'vue-router'
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth'
import store from '@/store'
import { forcePortalLogout, isAuthSessionError } from '@/utils/authSessionRecovery'
import LandingPage from '@/views/MainPages/LandingPage.vue'
import UsersPage from '@/views/ChildPages/UsersPage.vue'
import TeamsPage from '@/views/ChildPages/TeamsPage.vue'
import OrgDetailsPage from '@/views/ChildPages/OrgDetailsPage.vue'
import KnowledgeBasePage from '@/views/ChildPages/KnowledgeBasePage.vue'
import CallHistoryPage from '@/views/ChildPages/CallHistoryPage.vue'
import CallersPage from '@/views/ChildPages/CallersPage.vue'
import DashboardPage from '@/views/ChildPages/DashboardPage.vue'
import TransactionsPage from '@/views/ChildPages/TransactionsPage.vue'
import ApiKeysPage from '@/views/ChildPages/ApiKeysPage.vue'
import BusinessQueriesPage from '@/views/ChildPages/BusinessQueriesPage.vue'
import BulkUploadHistoryPage from '@/views/ChildPages/BulkUploadHistoryPage.vue'
import ContextsPage from '@/views/ChildPages/ContextsPage.vue'
import CampaignsPage from '@/views/ChildPages/CampaignsPage.vue'
import CampaignDetailPage from '@/views/ChildPages/CampaignDetailPage.vue'
import ContextDetailPage from '@/views/ChildPages/ContextDetailPage.vue'

const appChildRoutes = [
  {
    path: 'dashboard',
    name: 'Dashboard',
    component: DashboardPage,
  },
  {
    path: 'users',
    name: 'Users',
    component: UsersPage,
  },
  {
    path: 'teams',
    name: 'Teams',
    component: TeamsPage,
  },
  {
    path: 'org-details',
    name: 'OrgDetails',
    component: OrgDetailsPage,
  },
  {
    path: 'knowledge-base',
    name: 'KnowledgeBase',
    component: KnowledgeBasePage,
  },
  {
    path: 'call-history',
    name: 'CallHistory',
    component: CallHistoryPage,
  },
  {
    path: 'callers',
    name: 'Callers',
    component: CallersPage,
  },
  {
    path: 'transactions',
    name: 'Transactions',
    component: TransactionsPage,
  },
  {
    path: 'api-keys',
    name: 'ApiKeys',
    component: ApiKeysPage,
  },
  {
    path: 'business-queries',
    name: 'BusinessQueries',
    component: BusinessQueriesPage,
  },
  {
    path: 'business-queries/upload-history',
    name: 'BulkUploadHistory',
    component: BulkUploadHistoryPage,
  },
  {
    path: 'contexts',
    name: 'Contexts',
    component: ContextsPage,
  },
  {
    path: 'contexts/:contextId',
    name: 'ContextDetail',
    component: ContextDetailPage,
  },
  {
    path: 'campaigns',
    name: 'Campaigns',
    component: CampaignsPage,
  },
  {
    path: 'campaigns/:campaignId',
    name: 'CampaignDetail',
    component: CampaignDetailPage,
  },
  {
    path: 'tasks',
    redirect: { name: 'Campaigns' },
  },
  {
    path: 'tasks/:taskId',
    redirect: (to) => ({
      name: 'CampaignDetail',
      params: { campaignId: to.params.taskId },
      query: to.query,
    }),
  },
]

const routes = [
  {
    path: '/',
    component: LandingPage,
    children: appChildRoutes,
  },
  {
    path: '/login',
    redirect: '/',
  },
  {
    path: '/home',
    redirect: '/dashboard',
  },
  {
    path: '/home/:pathMatch(.*)*',
    redirect: (to) => {
      const rest = to.params.pathMatch
      const suffix = Array.isArray(rest) ? rest.join('/') : String(rest || '')
      return suffix ? `/${suffix}` : '/dashboard'
    },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, from, next) => {
  let isAuthenticated = false

  try {
    await getCurrentUser()
    const session = await fetchAuthSession()
    const hasToken = Boolean(session.tokens?.idToken)
    if (!hasToken) {
      throw new Error('No active session')
    }
    isAuthenticated = true
  } catch (error) {
    if (isAuthSessionError(error) || store.state.isAuthenticated) {
      await forcePortalLogout(store, { router })
    }
    isAuthenticated = false
  }

  store.commit('SET_AUTH_READY', true)
  store.commit('SET_AUTHENTICATED', isAuthenticated)

  if (!isAuthenticated) {
    next()
    return
  }

  if (to.path === '/' || to.path === '/login') {
    next({ name: 'Dashboard', replace: true })
    return
  }

  next()
})

export default router
